import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { generateMathFunctionSql } from '../utils/sqlGenerator';

interface MathFunctionBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

interface MathFunction {
  value: string;
  label: string;
  params: Array<{
    name: string;
    label: string;
    type: 'number' | 'select';
    default?: number | string;
    options?: Array<{ value: string; label: string }>;
  }>;
  example: string;
}

const MATH_FUNCTIONS: MathFunction[] = [
  {
    value: 'ROUND',
    label: 'ROUND - Round to decimals',
    params: [
      { name: 'decimals', label: 'Decimal Places', type: 'number', default: 2 }
    ],
    example: 'ROUND(3.14159, 2) → 3.14'
  },
  {
    value: 'FLOOR',
    label: 'FLOOR - Round down',
    params: [],
    example: 'FLOOR(3.9) → 3'
  },
  {
    value: 'CEIL',
    label: 'CEIL - Round up',
    params: [],
    example: 'CEIL(3.1) → 4'
  },
  {
    value: 'ABS',
    label: 'ABS - Absolute value',
    params: [],
    example: 'ABS(-5) → 5'
  },
  {
    value: 'SQRT',
    label: 'SQRT - Square root',
    params: [],
    example: 'SQRT(16) → 4'
  },
  {
    value: 'POWER',
    label: 'POWER - Raise to power',
    params: [
      { name: 'exponent', label: 'Exponent', type: 'number', default: 2 }
    ],
    example: 'POWER(2, 3) → 8'
  },
  {
    value: 'MOD',
    label: 'MOD - Modulo (remainder)',
    params: [
      { name: 'divisor', label: 'Divisor', type: 'number', default: 2 }
    ],
    example: 'MOD(10, 3) → 1'
  },
  {
    value: 'ARITHMETIC',
    label: 'ARITHMETIC - Basic operations',
    params: [
      {
        name: 'operation',
        label: 'Operation',
        type: 'select',
        default: 'multiply',
        options: [
          { value: 'add', label: 'Add (+)' },
          { value: 'subtract', label: 'Subtract (-)' },
          { value: 'multiply', label: 'Multiply (*)' },
          { value: 'divide', label: 'Divide (/)' },
        ]
      },
      { name: 'operand', label: 'Value', type: 'number', default: 1 }
    ],
    example: 'price * 1.1 (add 10%)'
  },
];

export const MathFunctionBuilder: React.FC<MathFunctionBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [selectedFunction, setSelectedFunction] = useState('ROUND');
  const [parameters, setParameters] = useState<Record<string, any>>({
    decimals: 2,
  });

  const currentFunction = MATH_FUNCTIONS.find(f => f.value === selectedFunction);

  useEffect(() => {
    const sql = generateMathFunctionSql(columnName, selectedFunction, parameters);
    onSqlGenerate(sql);
  }, [selectedFunction, parameters, columnName, onSqlGenerate]);

  const handleParameterChange = (paramName: string, value: any) => {
    setParameters(prev => ({ ...prev, [paramName]: value }));
  };

  // Reset parameters to the new function's defaults whenever selectedFunction
  // changes — converted from a useEffect to a guarded render-time adjustment
  // (react-hooks/set-state-in-effect). `hasInitializedParams` mirrors the
  // effect always firing once after mount (regardless of the initial value),
  // and `prevSelectedFunctionForParams` mirrors the `[selectedFunction]` dep;
  // both are set unconditionally inside the guard so it converges in one
  // extra render. Preserves the original's on-mount reset (which replaced
  // `parameters` with a fresh, content-equal object and so caused the
  // `onSqlGenerate` effect above to re-fire once more with the same SQL).
  const [hasInitializedParams, setHasInitializedParams] = useState(false);
  const [prevSelectedFunctionForParams, setPrevSelectedFunctionForParams] = useState(selectedFunction);
  if (!hasInitializedParams || selectedFunction !== prevSelectedFunctionForParams) {
    setHasInitializedParams(true);
    setPrevSelectedFunctionForParams(selectedFunction);
    if (currentFunction) {
      const newParams: Record<string, any> = {};
      currentFunction.params.forEach(param => {
        newParams[param.name] = param.default;
      });
      setParameters(newParams);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <Label>Math Function</Label>
        <Select value={selectedFunction} onValueChange={setSelectedFunction}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MATH_FUNCTIONS.map((func) => (
              <SelectItem key={func.value} value={func.value}>
                {func.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {currentFunction && currentFunction.params.length > 0 && (
        <div className="space-y-3">
          {currentFunction.params.map((param) => (
            <div key={param.name}>
              <Label>{param.label}</Label>
              {param.type === 'select' && param.options ? (
                <Select
                  value={String(parameters[param.name] || param.default)}
                  onValueChange={(value) => handleParameterChange(param.name, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {param.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="number"
                  value={parameters[param.name] ?? param.default}
                  onChange={(e) => handleParameterChange(
                    param.name,
                    parseFloat(e.target.value) || 0
                  )}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {currentFunction && (
        <div className="text-sm text-muted-foreground">
          <strong>Example:</strong>
          <div className="mt-1">{currentFunction.example}</div>
        </div>
      )}
    </div>
  );
};
